import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader,
  WhatsAppBanner, NoireBanner 
} from '@/components/layout/MobileLayout';
import { mockWishlist } from '@/lib/mockData';
import { formatDate } from '@/lib/utils';

// Wishlist Event Card
const WishlistCard = ({ event }) => (
  <Card className="bg-card border-border overflow-hidden group">
    <div className="relative aspect-video">
      <img 
        src={event.image} 
        alt={event.title}
        className="w-full h-full object-cover"
      />
      <button className="absolute top-3 right-3 p-2 bg-black/50 rounded-full hover:bg-red-500 transition-colors">
        <Heart className="w-5 h-5 text-white fill-white" />
      </button>
    </div>
    <div className="p-4">
      <h3 className="font-semibold text-white mb-2 group-hover:text-primary transition-colors">
        {event.title}
      </h3>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
        <Calendar className="w-4 h-4" />
        <span>{formatDate(event.date)}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <MapPin className="w-4 h-4" />
        <span>{event.venue}, {event.city}</span>
      </div>
      <div className="flex gap-2">
        <Link to={`/events/${event.id}`} className="flex-1">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
            View Event
          </Button>
        </Link>
        <Button variant="outline" size="icon" className="border-border hover:border-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </Card>
);

// Empty State
const EmptyWishlist = () => (
  <Card className="bg-card border-border p-12 text-center">
    <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
    <h2 className="text-xl font-semibold text-white mb-2">Your wishlist is empty</h2>
    <p className="text-muted-foreground mb-6">Save events you&apos;re interested in to find them easily later</p>
    <Link to="/">
      <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
        Browse Events
      </Button>
    </Link>
  </Card>
);

export function UserWishlist() {
  return (
    <div className="min-h-screen bg-background" data-testid="user-wishlist">
      {/* Mobile Layout */}
      <div className="lg:hidden">
        <MobileHeader />
        
        <main className="pt-14 pb-24 px-4">
          {/* WhatsApp Banner at top */}
          <div className="mt-4 mb-6">
            <WhatsAppBanner />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">My Wishlist</h1>
          <p className="text-muted-foreground mb-6">
            {mockWishlist.length} events saved
          </p>

          {mockWishlist.length === 0 ? (
            <EmptyWishlist />
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {mockWishlist.map(event => (
                <WishlistCard key={event.id} event={event} />
              ))}
            </div>
          )}

          {/* Noire Banner at bottom */}
          <div className="mt-6">
            <NoireBanner />
          </div>
        </main>

        <MobileBottomNav />
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <DesktopHeader />
        
        <main className="pt-20 pb-12">
          <div className="container mx-auto px-4">
            {/* WhatsApp banner at top - Desktop (FULL WIDTH) */}
            <div className="mb-8">
              <WhatsAppBanner />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">My Wishlist</h1>
            <p className="text-muted-foreground mb-8">
              {mockWishlist.length} events saved
            </p>

            {mockWishlist.length === 0 ? (
              <EmptyWishlist />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mockWishlist.map(event => (
                  <WishlistCard key={event.id} event={event} />
                ))}
              </div>
            )}

            {/* Noire banner at bottom - Desktop */}
            <div className="mt-10">
              <NoireBanner />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default UserWishlist;
